// src/components/form/TopUpPrefillWizard.jsx
// -----------------------------------------
// Stock Top-ups Phase 5 (shelter side). The deep-link / dashboard-banner target: after
// login, load THIS service's own pending staff-prepared prefill — authorised by the
// session / RLS (user_can_use_shelter_id), NEVER by a bearer token in the URL — and open
// the New Request wizard pre-filled and READ-ONLY on that top-up. The service reviews its
// own step-1 envelope (delivery / contact) as normal and submits via the standard member
// path, which consumes the prefill (submit_stock_top_up_atomic, db-164). If there is no
// pending prefill (stale link / already submitted), it shows a short message instead.
// Mounted by portal.jsx for /requests/new?prefill=1.
import React from "react";
import { listPendingPrefillsForShelter } from "../../data/stockTopUpPrefill";
import { Btn, Card } from "../../utils/ui.jsx";
import { RequestWizard } from "./RequestWizard.jsx";

// A default Client Reference for a staff-prepared top-up (the shelter can still edit it).
// Kept ≤ 25 chars to fit the Client Reference limit — "Top-up agreed dd/mm/yyyy" = 24.
function topUpClientRef(createdAt) {
  const d = createdAt ? new Date(createdAt) : new Date();
  const use = Number.isNaN(d.getTime()) ? new Date() : d;
  const dd = String(use.getDate()).padStart(2, "0");
  const mm = String(use.getMonth() + 1).padStart(2, "0");
  return `Top-up agreed ${dd}/${mm}/${use.getFullYear()}`;
}

export function TopUpPrefillWizard({ user, onBack, onSaveDraft, saveDraftRef, getPendingDraftId }) {
  const [loading, setLoading] = React.useState(true);
  const [prefill, setPrefill] = React.useState(null);

  React.useEffect(
    function () {
      let alive = true;
      const sid = user?.shelterUuid;
      if (!sid) {
        setLoading(false);
        return undefined;
      }
      listPendingPrefillsForShelter(sid).then(function (rows) {
        if (alive) {
          setPrefill(rows?.[0] || null);
          setLoading(false);
        }
      });
      return function () {
        alive = false;
      };
    },
    [user?.shelterUuid]
  );

  if (loading) {
    return (
      <div className="p-6 max-w-[var(--page-max)] mx-auto">
        <Card style={{ padding: 20 }}>Loading your prepared top-up…</Card>
      </div>
    );
  }

  if (!prefill) {
    return (
      <div className="p-6 max-w-[var(--page-max)] mx-auto">
        <Card style={{ padding: 20 }}>
          <p className="m-0 mb-3 text-[14px] text-zephyr-text">
            There's no prepared top-up waiting for you right now — it may have already been
            submitted.
          </p>
          <Btn onClick={onBack}>Back to My Requests</Btn>
        </Card>
      </div>
    );
  }

  return (
    <RequestWizard
      onBack={onBack}
      onSaveDraft={onSaveDraft}
      saveDraftRef={saveDraftRef}
      getPendingDraftId={getPendingDraftId}
      initialData={{
        requestType: "stock_top_up",
        topUpState: prefill.payload,
        prefillId: prefill.id,
        // Phase 5: a sensible default Client Reference for a staff-prepared top-up
        // (editable). The step-1 contact + delivery seed FRESH from the caseworker
        // session inside RequestWizard (isPrefill branch), not from here.
        shelterRef: topUpClientRef(prefill.created_at),
      }}
    />
  );
}
